import { getIn, useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { IVehicleRegistrationFormValues } from 'features/Vehicles/types'
import { Label } from 'components/v2/Label'
import { InputDate } from 'components/v2/InputDate'
import { SelectState } from 'features/Cities/components/SelectState'
import { STATES } from 'utils/states'
import { Select } from 'components/v2/Select'
import { safeFormatMessage } from '@10xlabs/utils'
import { registrationCategoryMessages } from 'features/Vehicles/messages'
import { GTVehicleRegistrationCategoryInput } from 'gql/types.generated'
import { FilePreview, InputFile, MB } from 'components/v2/InputFile'
import { Checkbox } from 'components/v2/Checkbox'
import styled from 'styled-components'

const messages = defineMessages({
  registrationTypePlaceholder: {
    id: 'vehicles.form.registration.type.placeholder',
    defaultMessage: 'Select type'
  },
  registrationCategoryPlaceholder: {
    id: 'vehicles.form.registration.category.placeholder',
    defaultMessage: 'Select category'
  }
})

const CheckboxContainer = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 5px;
  gap: 8px;
`

const REGISTRATION_CATEGORY_LIST = [
  GTVehicleRegistrationCategoryInput.Federal,
  GTVehicleRegistrationCategoryInput.Local,
  GTVehicleRegistrationCategoryInput.DrivingPermit
]

export const VehicleRegistrationForm = (): ReactElement => {
  const intl = useIntl()
  const { values, setFieldValue, handleChange, handleBlur, touched, errors } =
    useFormikContext<IVehicleRegistrationFormValues>()

  return (
    <>
      <Heading.H4>
        <FormattedMessage id="vehicles.form.registration.title" defaultMessage="Vehicle registration" />
      </Heading.H4>
      <Row noMargin>
        <Column>
          <Label>
            <FormattedMessage id="vehicles.form.registration.category.label" defaultMessage="Category" />
          </Label>
          <Select
            id="registration.category"
            value={values.registration.category || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.registration?.category}
            errors={errors.registration?.category}
          >
            <option value="">{intl.formatMessage(messages.registrationCategoryPlaceholder)}</option>
            {REGISTRATION_CATEGORY_LIST.map((category): ReactElement => {
              return (
                <option key={category} value={category}>
                  {safeFormatMessage({ intl, messages: registrationCategoryMessages, key: category })}
                </option>
              )
            })}
          </Select>
        </Column>
        <Column>
          <Label>
            <FormattedMessage id="vehicles.form.registration.expirationDate.label" defaultMessage="Expiration date" />
          </Label>
          <CheckboxContainer>
            <Checkbox
              id="vehicles.form.registration.permanentExpirationDate"
              checked={values.registration.isPermanent}
              onChange={({ target: { checked } }): void => {
                setFieldValue('registration.isPermanent', checked)
                setFieldValue('registration.expirationDate', '')
              }}
            />
            <FormattedMessage
              id="vehicles.form.registration.permanentExpirationDate.label"
              defaultMessage="Permanent"
            />
          </CheckboxContainer>
          <InputDate
            id="registration.expirationDate"
            intl={intl}
            placeholder="--/--/----"
            disabled={values.registration.isPermanent}
            value={values.registration.expirationDate}
            onChange={(value?: Date): void => {
              setFieldValue('registration.expirationDate', value)
            }}
            touched={getIn(touched, 'registration.expirationDate')}
            errors={getIn(errors, 'registration.expirationDate')}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Label>
            <FormattedMessage id="vehicles.form.registration.state.label" defaultMessage="State" />
          </Label>
          <SelectState
            id="registration.state"
            value={values.registration.state?.code || ''}
            onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
              const state = STATES.find((state) => state.code === value) || null
              setFieldValue('registration.state', state)
            }}
            touched={getIn(touched, 'registration.state')}
            errors={getIn(errors, 'registration.state')}
          />
        </Column>
        <Column width="50%">
          <Row>
            <Column>
              <Label>
                <FormattedMessage id="vehicles.form.registration.document.label" defaultMessage="Document attach" />
              </Label>
              <InputFile
                onChange={(file): void => {
                  if (file instanceof File) {
                    setFieldValue('registration.document', {
                      file,
                      name: file.name,
                      path: ''
                    })
                  }
                }}
                touched={getIn(touched, 'registration.document')}
                errors={getIn(errors, 'registration.document')}
                accept={{
                  'application/pdf': [],
                  'image/jpeg': [],
                  'image/jpg': [],
                  'image/png': []
                }}
                maxSize={16 * MB}
              />
            </Column>
          </Row>
          {(values.registration.document?.file || values.registration.document?.path) && (
            <Row>
              <Column>
                <FilePreview
                  file={values.registration.document.file || (values.registration.document.path as string)}
                  filename={values.registration.document.name}
                  onRemoveFile={(): void => {
                    setFieldValue('registration.document', {
                      file: '',
                      name: '',
                      path: ''
                    })
                  }}
                />
              </Column>
            </Row>
          )}
        </Column>
      </Row>
    </>
  )
}
